import { useState, useRef, useEffect, useCallback } from "react";
import { Logo } from "@/components/Logo";
import { Chat } from "@/components/chat/Chat";
import { faTimes } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { motion, AnimatePresence, useMotionValue } from "motion/react";
import { sendChatMessage } from "@/services/api/scenarios";
import { sendDocumentChatQuery } from "@/services/api/documents";
import { sendCashFlowChatMessage } from "@/services/api/cash-flow";
import { sendDashboardChatMessage } from "@/services/api/dashboard";
import type { ChatResponse, ChatMessage } from "@/services/api/scenarios/types";
import type { DocumentChatResponse } from "@/services/api/documents/types";
import type { DashboardChatResponse } from "@/services/api/dashboard/types";
import { useAPI } from "@/context/APIContext";
import { sendPodChatMessage } from "@/services/api/agentic";
import { useAni, type AniChatWidgetContextValue } from "@/context/AniContext";
import { useChatContextConfig } from "@/hooks/useChatContextConfig";
import { useRichAniContext } from "@/hooks/useRichAniContext";
import { useAuth } from "@/context/AuthContext";
import { isAniStaff } from "@/utils/aniPlatformAccess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faGear,
  faThumbtack,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";



interface AniChatWidgetProps {
  resourceId: string;
  /** Single source of truth for the chat context tag (see AniContext). */
  context?: AniChatWidgetContextValue;

  apiData?: unknown; // Data to be passed to the cash-flow expert
  initialRight?: string;
  initialBottom?: string;
  onApiAction?: (action: {
    endpoint: string;
    method: string;
    data: unknown;
    explanation: string;
  }) => void;
  contentAreaRef?: React.RefObject<HTMLDivElement | null>;
  onCutoutInteraction?: (action: "leave" | "enter_proximity") => void;
  isCutoutCurrentlyOpen?: boolean;
  onDragStateChange?: (isDragging: boolean) => void;
  samplePrompts?: string[];
  initialAssistantMessage?: string;
}

interface ChatComponentMessage {
  id: string;
  content: string;
  role: "assistant" | "user";
  timestamp: number;
  sender: {
    id: string;
    name: string;
    type: string;
  };
}

// Common response type for both APIs
type ChatResponseType =
  | ChatResponse
  | DocumentChatResponse
  | DashboardChatResponse
  | {
      answer: string;
      api_action?: {
        endpoint: string;
        method: string;
        data: unknown;
        explanation: string;
      };
    };

type AniApiAction = {
  endpoint: string;
  method: string;
  data: unknown;
  explanation: string;
};

// Constants for sizing
const EXPANDED_WIDTH_PX = 400;
const COLLAPSED_SIZE_PX = 50; // Previous hardcoded collapsed size
const MIN_FLOATING_WIDTH_PX = 320;
const MIN_FLOATING_HEIGHT_PX = 420;
const MAX_FLOATING_WIDTH_PX = 700;
const MAX_FLOATING_HEIGHT_PX = 900;

/** Show the Dev mode toggle in the Ani sidebar header only in development */
const DEV_MODE_ENABLED = import.meta.env.DEV;

// Helper to get root font size once, can be cached if called frequently in other contexts
// const getRootFontSize = () => parseFloat(getComputedStyle(document.documentElement).fontSize); // No longer needed for collapsed size

// Helper to get collapsed size in pixels
// const getCollapsedSizePx = () => getRootFontSize() * COLLAPSED_SIZE_REM_VALUE; // Replaced

export function AniChatWidget({
  resourceId,
  context,
  apiData,
  initialRight = "1rem",
  initialBottom = "1rem",
  onApiAction,
  contentAreaRef: _contentAreaRef,
  onCutoutInteraction: _onCutoutInteraction,
  isCutoutCurrentlyOpen: _isCutoutCurrentlyOpen,
  onDragStateChange,
  samplePrompts,
  initialAssistantMessage,
}: AniChatWidgetProps) {
  const { request } = useAPI();
  const {
    samplePrompts: aniSamplePrompts,
    initialAssistantMessage: aniInitialAssistantMessage,
    widgetViewState,
    setWidgetViewState,
    setPersonalityMode,
    setDisplayMode,
    conversationHistory,
    addToConversationHistory,
    outboundUserMessage,
    clearOutboundUserMessage,
  } = useAni();
  const { user } = useAuth();
  const aniStaff = isAniStaff(user);

  // Get rich context information (route, tabs, visible widgets)
  const richContext = useRichAniContext();

  // Get context-specific configuration
  const {
    isActive: isContextActive,
    initialAssistantMessage: contextInitialMessage,
    samplePrompts: contextSamplePrompts,
  } = useChatContextConfig(context);

  // Determine which values to use (props > context config > ani context > defaults)
  const finalSamplePrompts =
    samplePrompts || contextSamplePrompts || aniSamplePrompts || [];
  const finalInitialMessage =
    initialAssistantMessage ||
    contextInitialMessage ||
    aniInitialAssistantMessage ||
    "";

  // Track the active context to detect changes
  const [activeContext, setActiveContext] = useState<
    AniChatWidgetContextValue | undefined
  >(context);
  const [, setContextChangeMessage] = useState<string>("");
  const [, setShowContextBanner] = useState(false);

  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  // REMOVED: local conversationHistory state - now managed by AniContext
  const [isResponding, setIsResponding] = useState(false);
  const [, setIsThinking] = useState(false);
  const [, setCurrentSpokenMessage] = useState("");

  // Context labels mapping
  const contextLabels: Record<string, string> = {
    scenarios: "Scenario Tracker",
    documents: "Documents",
    "cash-flow": "Cash Flow",
    pod: "Pod",
    dashboard: "Dashboard",
    screener: "Screener",
    playground: "Playground",
    product: "Product",
    portfolio: "Portfolio",
    worker: "Worker",
    "report-builder": "Report Builder",
  };

  // Get the current context label
  const currentContextLabel = context ? contextLabels[context] || context : "General";

  // Detect context changes and show notification
  useEffect(() => {
    if (context !== activeContext) {
      setContextChangeMessage(`Now chatting about: ${currentContextLabel}`);
      setShowContextBanner(true);
      setActiveContext(context);

      // Hide banner after 3 seconds
      const timer = setTimeout(() => {
        setShowContextBanner(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [context, activeContext, currentContextLabel]);

  // Use a ref to get the latest isResponding value inside the stable callback
  const isRespondingRef = useRef(isResponding);
  useEffect(() => {
    isRespondingRef.current = isResponding;
  }, [isResponding]);

  // "Thinking" is when the user has sent a message and we are waiting for a response.
  const handleTypingChange = useCallback(
    (isTyping: boolean) => {
      // We are thinking if the child chat is typing, but only if we aren't already speaking.
      if (!isResponding) {
        setIsThinking(isTyping);
      }
    },
    [isResponding]
  );

  // Handle response completion - clear state when message is fully displayed
  useEffect(() => {
    // If we were responding and now have a message, we're done responding
    if (isResponding && conversationHistory.length > 0) {
      const lastMessage = conversationHistory[conversationHistory.length - 1];
      if (lastMessage.role === "assistant") {
        // Give a brief moment for the animation to complete
        const timer = setTimeout(() => {
          setIsResponding(false);
          setIsThinking(false);
          setCurrentSpokenMessage("");
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [conversationHistory, isResponding]);

  const isExpanded = widgetViewState.isOpen;
  const chatDisplayMode =
    aniStaff && widgetViewState.displayMode === "developer" ? "developer" : "normal";
  const isFloating = widgetViewState.mode === "floating";
  const isDockedDesktop = isExpanded && isDesktop && !isFloating && widgetViewState.isSpaceReady;
  const isMobileDrawer = isExpanded && !isDesktop;

  // Motion values for floating mode positioning/sizing - created once, synced via effects
  const motionLeft = useMotionValue(0);
  const motionTop = useMotionValue(0);
  const motionWidth = useMotionValue(400);
  const motionHeight = useMotionValue(600);

  const dragStartOffsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const onResizeInternalRef = useRef<((event: MouseEvent) => void) | null>(null);
  const onResizeStopRef = useRef<(() => void) | null>(null);
  const resizeRef = useRef<{
    direction: "right" | "bottom" | "corner";
    startX: number;
    startY: number;
    width: number;
    height: number;
  } | null>(null);
  const draggableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const onMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };
    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", onMediaChange);
    return () => mediaQuery.removeEventListener("change", onMediaChange);
  }, []);

  // Ensure isSpaceReady is true for mobile drawer and floating mode
  useEffect(() => {
    if (isExpanded && (!isDesktop || isFloating)) {
      setWidgetViewState({ isSpaceReady: true });
    }
  }, [isExpanded, isDesktop, isFloating, setWidgetViewState]);

  const parseCssValueToPx = useCallback((valueStr: string): number => {
    if (!valueStr) return 0;
    if (valueStr.endsWith("px")) return parseFloat(valueStr);
    if (valueStr.endsWith("rem")) {
      return (
        parseFloat(valueStr) *
        parseFloat(getComputedStyle(document.documentElement).fontSize)
      );
    }
    const numericValue = parseFloat(valueStr);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  }, []);

  const clampFloatingPosition = useCallback(
    (x: number, y: number, width: number, height: number) => {
      const minX = 0;
      const minY = 0;
      const maxX = Math.max(minX, window.innerWidth - width);
      const maxY = Math.max(minY, window.innerHeight - height);
      return {
        x: Math.min(Math.max(minX, x), maxX),
        y: Math.min(Math.max(minY, y), maxY),
      };
    },
    []
  );

  const moveToCollapsedAnchor = useCallback(() => {
    const rightOffset = parseCssValueToPx(initialRight || "0");
    const bottomOffset = parseCssValueToPx(initialBottom || "0");
    const targetX = window.innerWidth - COLLAPSED_SIZE_PX - rightOffset;
    const targetY = window.innerHeight - COLLAPSED_SIZE_PX - bottomOffset;
    const clamped = clampFloatingPosition(
      targetX,
      targetY,
      COLLAPSED_SIZE_PX,
      COLLAPSED_SIZE_PX
    );
    motionLeft.set(clamped.x);
    motionTop.set(clamped.y);
  }, [clampFloatingPosition, initialBottom, initialRight, motionLeft, motionTop, parseCssValueToPx]);

  useEffect(() => {
    if (!isExpanded && !isFloating) {
      moveToCollapsedAnchor();
    }
  }, [isExpanded, isFloating, moveToCollapsedAnchor]);

  const persistFloatingState = useCallback(() => {
    setWidgetViewState({
      floatingPosition: {
        x: motionLeft.get(),
        y: motionTop.get(),
      },
      floatingSize: {
        width: motionWidth.get(),
        height: motionHeight.get(),
      },
    });
  }, [motionHeight, motionLeft, motionTop, motionWidth, setWidgetViewState]);

  useEffect(() => {
    if (!isFloating) {
      return;
    }

    motionLeft.set(widgetViewState.floatingPosition.x);
    motionTop.set(widgetViewState.floatingPosition.y);
    motionWidth.set(widgetViewState.floatingSize.width);
    motionHeight.set(widgetViewState.floatingSize.height);
  }, [
    isFloating,
    motionHeight,
    motionLeft,
    motionTop,
    motionWidth,
    widgetViewState.floatingPosition.x,
    widgetViewState.floatingPosition.y,
    widgetViewState.floatingSize.height,
    widgetViewState.floatingSize.width,
  ]);

  useEffect(() => {
    const onWindowResize = () => {
      if (isFloating) {
        const clampedPosition = clampFloatingPosition(
          motionLeft.get(),
          motionTop.get(),
          motionWidth.get(),
          motionHeight.get()
        );
        motionLeft.set(clampedPosition.x);
        motionTop.set(clampedPosition.y);
        persistFloatingState();
      } else if (!isExpanded) {
        moveToCollapsedAnchor();
      }
    };

    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [
    clampFloatingPosition,
    isExpanded,
    isFloating,
    motionHeight,
    motionLeft,
    motionTop,
    motionWidth,
    moveToCollapsedAnchor,
    persistFloatingState,
  ]);

  const openWidget = useCallback(() => {
    setWidgetViewState({ isOpen: true, isSpaceReady: false });
    
    // After 200ms (before padding completes), start fading in the widget
    setTimeout(() => {
      setWidgetViewState({ isSpaceReady: true });
    }, 200);
  }, [setWidgetViewState]);

  const startDrag = (event: React.MouseEvent) => {
    if (!isFloating || !isExpanded) {
      return;
    }
    const target = event.target as HTMLElement;
    if (
      target.tagName === "BUTTON" ||
      target.tagName === "A" ||
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT" ||
      target.closest("[data-no-drag='true']")
    ) {
      return;
    }

    event.preventDefault();
    motionLeft.stop();
    motionTop.stop();
    isDraggingRef.current = false;
    dragStartOffsetRef.current = {
      x: event.clientX - motionLeft.get(),
      y: event.clientY - motionTop.get(),
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        setIsDragging(true);
        onDragStateChangeRef.current?.(true);
        document.body.style.cursor = "grabbing";
      }

      e.preventDefault();
      document.body.style.cursor = "grabbing";

      const x = e.clientX - dragStartOffsetRef.current.x;
      const y = e.clientY - dragStartOffsetRef.current.y;
      const clamped = clampFloatingPositionRef.current(
        x,
        y,
        motionWidth.get(),
        motionHeight.get()
      );
      motionLeft.set(clamped.x);
      motionTop.set(clamped.y);
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        setIsDragging(false);
        onDragStateChangeRef.current?.(false);
        persistFloatingStateRef.current();
      }
      document.body.style.cursor = "default";
      isDraggingRef.current = false;

      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const clampFloatingPositionRef = useRef(clampFloatingPosition);
  useEffect(() => {
    clampFloatingPositionRef.current = clampFloatingPosition;
  }, [clampFloatingPosition]);

  const persistFloatingStateRef = useRef(persistFloatingState);
  useEffect(() => {
    persistFloatingStateRef.current = persistFloatingState;
  }, [persistFloatingState]);

  const onDragStateChangeRef = useRef(onDragStateChange);
  useEffect(() => {
    onDragStateChangeRef.current = onDragStateChange;
  }, [onDragStateChange]);

  const startResize = (
    event: React.MouseEvent,
    direction: "right" | "bottom" | "corner"
  ) => {
    if (!isFloating || !isExpanded) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsResizing(true);

    resizeRef.current = {
      direction,
      startX: event.clientX,
      startY: event.clientY,
      width: motionWidth.get(),
      height: motionHeight.get(),
    };

    window.addEventListener("mousemove", onResizeInternal);
    window.addEventListener("mouseup", onResizeStop);
  };

  const onResizeInternal = useCallback((event: MouseEvent) => {
    if (!resizeRef.current) return;

    const deltaX = event.clientX - resizeRef.current.startX;
    const deltaY = event.clientY - resizeRef.current.startY;

    let nextWidth = resizeRef.current.width;
    let nextHeight = resizeRef.current.height;

    const maxWidth = Math.min(MAX_FLOATING_WIDTH_PX, window.innerWidth - 16);
    const maxHeight = Math.min(MAX_FLOATING_HEIGHT_PX, window.innerHeight - 16);

    if (
      resizeRef.current.direction === "right" ||
      resizeRef.current.direction === "corner"
    ) {
      nextWidth = Math.min(
        Math.max(MIN_FLOATING_WIDTH_PX, resizeRef.current.width + deltaX),
        maxWidth
      );
    }

    if (
      resizeRef.current.direction === "bottom" ||
      resizeRef.current.direction === "corner"
    ) {
      nextHeight = Math.min(
        Math.max(MIN_FLOATING_HEIGHT_PX, resizeRef.current.height + deltaY),
        maxHeight
      );
    }

    const clampedPosition = clampFloatingPositionRef.current(
      motionLeft.get(),
      motionTop.get(),
      nextWidth,
      nextHeight
    );

    motionLeft.set(clampedPosition.x);
    motionTop.set(clampedPosition.y);
    motionWidth.set(nextWidth);
    motionHeight.set(nextHeight);
  }, [motionHeight, motionLeft, motionTop, motionWidth]);

  useEffect(() => {
    onResizeInternalRef.current = onResizeInternal;
  }, [onResizeInternal]);

  const onResizeStop = useCallback(() => {
    setIsResizing(false);
    resizeRef.current = null;
    persistFloatingStateRef.current();
    if (onResizeInternalRef.current) {
      window.removeEventListener("mousemove", onResizeInternalRef.current);
    }
    if (onResizeStopRef.current) {
      window.removeEventListener("mouseup", onResizeStopRef.current);
    }
  }, []);

  useEffect(() => {
    onResizeStopRef.current = onResizeStop;
  }, [onResizeStop]);

  const handleClose = () => {
    // For docked desktop mode, animate widget out first, then remove space
    if (isDesktop && !isFloating) {
      setWidgetViewState({ isSpaceReady: false });
      // After widget animates out (~300ms), close completely and animate space back
      setTimeout(() => {
        setWidgetViewState({ isOpen: false });
      }, 300);
    } else {
      // For mobile/floating, close immediately
      setWidgetViewState({ isOpen: false });
    }
  };

  // Function to execute an API action
  const executeApiAction = async (action: {
    endpoint: string;
    method: string;
    data: unknown;
    explanation: string;
  }) => {
    try {
      console.log(`Executing API action: ${action.method} ${action.endpoint}`);
      console.log("Action data:", action.data);

      const response = await request({
        method: action.method,
        url: action.endpoint,
        data: action.data,
      });

      console.log("API action response:", response);

      // Call the callback if provided
      if (onApiAction) {
        const payload =
          typeof response === "object" &&
          response !== null &&
          "api_action" in response
            ? (response as { api_action: Parameters<NonNullable<typeof onApiAction>>[0] }).api_action
            : undefined;
        if (payload) {
          await onApiAction(payload);
        }
      }

      return response as ChatResponseType;
    } catch (error) {
      console.error("Error executing API action:", error);
      throw error;
    }
  };

  const panelContent = (
    <div
      className="absolute inset-0  "
      data-testid="ani-widget-panel"
    >
      {/* <AnimatedBlobsBackground opacity={0.07} heightRatio={0.7} /> */}

      {/* Context change banner */}
      {/* <AnimatePresence>
        {showContextBanner && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="absolute top-2 left-4 right-4 z-20 bg-gradient-to-r from-blue-500/90 to-purple-500/90 text-white text-sm px-4 py-2 rounded-lg shadow-lg backdrop-blur-sm"
            data-testid="context-change-banner"
          >
            {contextChangeMessage}
          </motion.div>
        )}
      </AnimatePresence> */}

      <div className="pointer-events-none absolute transition-opacity duration-300 ease-in-out inset-x-6 -top-[32px] h-[64px] z-10">
        {/* <AniWaveform
          isActive={waveformIsActive && isExpanded}
          message={currentSpokenMessage}
          baseAmplitude={8}
          maxDetailAmplitude={12}
          pulseAmplitudeMultiplier={24}
          thinkingExcitement={0.2}
          speakingExcitement={1.0}
          backgroundWaveOpacity={0.8}
        /> */}
        {/* {isExpanded && (
          <div className="absolute top-[32px] left-0 right-0 h-[1px]">
            <div
              className={`absolute inset-0 bg-[linear-gradient(to_right,transparent_0%,#a7bffa_40%,#c084fc_50%,#a7bffa_60%,transparent_100%)] transition-opacity duration-300 ease-in-out ${
                waveformIsActive ? "opacity-0" : "opacity-100"
              }`}
            />
            <div
              className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent transition-opacity duration-300 ease-in-out ${
                waveformIsActive ? "opacity-100" : "opacity-0"
              }`}
            />
          </div>
        )} */}
      </div>

      <div
        className={`flex space-x-2 items-center justify-between p-5 px-0 border-b border-slate-200 dark:border-slate-700 ${
          isFloating ? "cursor-grab active:cursor-grabbing" : ""
        }`}
        onMouseDown={isFloating ? startDrag : undefined}
        data-testid="ani-widget-header"
      >
        <div className="font-mono font-medium text-lg text-gray-500 dark:text-gray-200 pr-2 pl-3 whitespace-nowrap flex items-center space-x-2 pointer-events-none">
          <img src="/images/logo.svg" alt="Ani" className="w-8 h-8 rounded-full" />
          <div className="flex flex-col">
            <div className="flex items-center font-sans space-x-1.5 font-medium text-white font-normal">
              
              <span className="font-medium">
                {currentContextLabel}
              </span>
            </div>
           
          </div>
        </div>

        <div className="flex space-x-2 px-2 pointer-events-auto items-center">
          {DEV_MODE_ENABLED &&
            aniStaff &&
            (widgetViewState.displayMode === "developer" ? (
              <button
                type="button"
                data-testid="ani-widget-exit-developer-menu"
                data-no-drag="true"
                aria-label="Back to chat"
                title="Back to chat"
                onClick={(event) => {
                  event.stopPropagation();
                  setDisplayMode("normal");
                }}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-gray-100"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                data-testid="ani-widget-developer-menu"
                data-no-drag="true"
                aria-label="Open Developer menu"
                title="Developer menu"
                onClick={(event) => {
                  event.stopPropagation();
                  setDisplayMode("developer");
                }}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-gray-100"
              >
                <FontAwesomeIcon icon={faGear} className="h-4 w-4" />
              </button>
            ))}

          {isDesktop && (
            <button
              type="button"
              data-testid="ani-widget-mode-toggle"
              data-no-drag="true"
              onClick={(event) => {
                event.stopPropagation();
                if (isFloating) {
                  setWidgetViewState({ mode: "docked", isSpaceReady: false });
                  // After 200ms (before padding completes), start fading in the widget
                  setTimeout(() => {
                    setWidgetViewState({ isSpaceReady: true });
                  }, 200);
                } else {
                  const floatingX = window.innerWidth - EXPANDED_WIDTH_PX - 24;
                  const floatingY = 16;
                  const clamped = clampFloatingPosition(
                    floatingX,
                    floatingY,
                    motionWidth.get(),
                    motionHeight.get()
                  );
                  motionLeft.set(clamped.x);
                  motionTop.set(clamped.y);
                  setWidgetViewState({
                    mode: "floating",
                    floatingPosition: clamped,
                    isSpaceReady: true, // Floating doesn't need space animation
                  });
                }
              }}
              className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 h-10 px-3 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
            >
              {!isFloating ? 
          
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-4 h-4" />
             
              : 
         
              <FontAwesomeIcon icon={faThumbtack} className="w-4 h-4" />
           
              }
            </button>
          )}
          <button
            type="button"
            data-testid="ani-widget-close"
            data-no-drag="true"
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
            }}
            className="cursor-pointer text-slate-300 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 h-10 w-10 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors text-2xl"
          >
          
            <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="expanded-content absolute inset-x-0 top-[70px] bottom-0 flex flex-col cursor-default">
        <Chat
          recipientId={resourceId}
          recipientName="Ani"
          recipientAvatar="ani"
          isAni={true}
          isMinimal={true}
          useDirectResponse={true}
          miniUpload={true}
          isContextActive={isContextActive}
          richContext={richContext}
          contextLabel={currentContextLabel}
          displayMode={chatDisplayMode}
          aniPersonalityMode={widgetViewState.personalityMode}
          onAniPersonalityModeChange={setPersonalityMode}
          sendMessage={async (recipientId, data): Promise<ChatComponentMessage> => {
            try {
              let responseAnswer: string;
              let apiActionPayload: AniApiAction | null = null;

              if (context === "pod") {
                // Pod uses a different response structure
                const podResponse = await sendPodChatMessage(
                  resourceId,
                  data.content,
                  []
                );
                responseAnswer = podResponse.content || "";
              } else {
                // All other contexts use the standard ChatResponseType
                const response: ChatResponseType =
                  context === "documents"
                    ? ((await sendDocumentChatQuery(
                        data.content,
                        conversationHistory
                      )) as ChatResponseType)
                    : context === "cash-flow"
                      ? ((await sendCashFlowChatMessage(
                          data.content,
                          apiData,
                          conversationHistory
                        )) as ChatResponseType)
                      : context === "dashboard"
                        ? ((await sendDashboardChatMessage(
                            resourceId,
                            data.content,
                            conversationHistory
                          )) as ChatResponseType)
                        : ((await sendChatMessage(
                            resourceId,
                            data.content,
                            conversationHistory
                          )) as ChatResponseType);

                responseAnswer = response.answer;
                
                if (
                  typeof response === "object" &&
                  response !== null &&
                  "api_action" in response &&
                  response.api_action
                ) {
                  apiActionPayload = response.api_action;
                }
              }

              // Handle API actions if present
              if (apiActionPayload) {
                if (onApiAction) {
                  try {
                    await onApiAction(apiActionPayload);
                    responseAnswer +=
                      "\n\n_I've updated your cash flow with the changes we discussed._";
                  } catch {
                    responseAnswer +=
                      "\n\n_I tried to update your cash flow, but encountered an error. Please try again._";
                  }
                } else if (context === "cash-flow") {
                  try {
                    await executeApiAction(apiActionPayload);
                    responseAnswer +=
                      "\n\n_I've updated your cash flow via local executeApiAction._";
                  } catch {
                    responseAnswer +=
                      "\n\n_I tried to update your cash flow via local executeApiAction, but encountered an error._";
                  }
                }
              }

              const userMessage: ChatMessage = {
                role: "user",
                content: data.content,
              };
              const assistantMessage: ChatMessage = {
                role: "assistant",
                content: responseAnswer,
              };

              setCurrentSpokenMessage(assistantMessage.content || "");
              addToConversationHistory([userMessage, assistantMessage]);

              return {
                id: `msg-${Date.now()}`,
                content: responseAnswer,
                role: "assistant",
                timestamp: Date.now(),
                sender: {
                  id: "ani",
                  name: "Ani",
                  type: "assistant",
                },
              };
            } catch (error) {
              setIsResponding(false);
              setIsThinking(false);
              setCurrentSpokenMessage("");
              console.error("Error sending message:", error);
              throw error;
            }
          }}
          onTypingChange={handleTypingChange}
          fetchHistory={async (_recipientId: string): Promise<ChatComponentMessage[]> => {
            return conversationHistory.map((message, index) => ({
              id: `msg-${index}`,
              content: message.content,
              role: message.role,
              timestamp: Date.now(),
              sender: {
                id: message.role === "assistant" ? "ani" : "user",
                name: message.role === "assistant" ? "Ani" : "User",
                type: message.role === "assistant" ? "assistant" : "user",
              },
            }));
          }}
          fetchSystemTasks={async () => {
            return [];
          }}
          samplePrompts={finalSamplePrompts}
          initialAssistantMessage={finalInitialMessage}
          outboundUserMessage={outboundUserMessage}
          onOutboundUserMessageConsumed={clearOutboundUserMessage}
        />
      </div>

      {isFloating && isDesktop && (
        <>
          <div
            data-testid="ani-resize-handle-right"
            className="absolute top-0 right-0 h-full w-3 cursor-ew-resize"
            onMouseDown={(event) => startResize(event, "right")}
            data-no-drag="true"
          />
          <div
            data-testid="ani-resize-handle-bottom"
            className="absolute left-0 bottom-0 w-full h-3 cursor-ns-resize"
            onMouseDown={(event) => startResize(event, "bottom")}
            data-no-drag="true"
          />
          <div
            data-testid="ani-resize-handle-corner"
            className="absolute right-0 bottom-0 w-5 h-5 cursor-nwse-resize"
            onMouseDown={(event) => startResize(event, "corner")}
            data-no-drag="true"
          />
        </>
      )}
    </div>
  );

  return (
    <>
      <AnimatePresence>
        {!isExpanded && (
          <motion.button
            type="button"
            data-testid="ani-widget-collapsed"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={openWidget}
            className="fixed z-[9999] w-[50px] h-[50px] rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-md flex items-center justify-center hover:shadow-lg transition-shadow"
            style={{
              left: motionLeft,
              top: motionTop,
            }}
          >
            <Logo iconOnly className="h-8 w-8" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobileDrawer && (
          <>
            <motion.button
              type="button"
              aria-label="Close ANI drawer backdrop"
              className="fixed inset-0 z-[9997] bg-black/35 backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
            />
            <motion.div
              ref={draggableRef}
              data-testid="ani-widget-mobile-drawer"
              initial={{ x: 28, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 28, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              className="fixed right-2 top-2 bottom-2 w-[min(420px,calc(100vw-1rem))] rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg z-[9998] overflow-hidden"
            >
              {panelContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isDockedDesktop && (
          <motion.aside
            ref={draggableRef}
            data-testid="ani-widget-docked"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: 0.35,
              ease: [0.32, 0.72, 0, 1]
            }}
            className="fixed right-4 top-4 bottom-4 w-[420px] rounded-3xl z-[9998] overflow-hidden"
          >
            {panelContent}
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isExpanded && isFloating && isDesktop && (
          <motion.div
            ref={draggableRef}
            data-testid="ani-widget-floating"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{
              opacity: 1,
              scale: isDragging || isResizing ? 1.01 : 1,
              boxShadow: isDragging || isResizing
                ? "0px 18px 36px rgba(15,23,42,0.2)"
                : "0px 10px 24px rgba(15,23,42,0.14)",
            }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="fixed rounded-3xl bg-white dark:bg-slate-700 shadow-lg shadow-black z-[9998] overflow-hidden"
            style={{
              left: motionLeft,
              top: motionTop,
              width: motionWidth,
              height: motionHeight,
            }}
          >
            {panelContent}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
